import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaMoon, FaSun } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { NAVIGATION_ITEMS } from '../constants';
import { useScrollPosition } from '../hooks/useScrollPosition';
import { useTheme } from '../theme/ThemeProvider';
import './Header.css';

const NAV_ID = 'main-navigation';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Higher threshold so mobile rubber-band / URL-bar jitter doesn't
  // immediately replace the transparent home header with a solid bar
  const { isScrolled, scrollDirection } = useScrollPosition(120);
  const { theme, toggleTheme } = useTheme();
  const menuToggleRef = useRef(null);
  const navRef = useRef(null);
  const previouslyFocused = useRef(null);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = useCallback(() => setIsMenuOpen((p) => !p), []);

  const handleLogoClick = useCallback(
    (e) => {
      e.preventDefault();
      if (location.pathname !== '/') {
        navigate('/');
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      closeMenu();
    },
    [location.pathname, navigate, closeMenu]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeMenu]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  const isHome = location.pathname === '/';

  useEffect(() => {
    document.documentElement.classList.toggle('home-hero', isHome);
    return () => document.documentElement.classList.remove('home-hero');
  }, [isHome]);

  // Focus trap + restore focus when mobile drawer opens/closes
  useEffect(() => {
    if (!isMenuOpen) {
      if (previouslyFocused.current?.focus) {
        previouslyFocused.current.focus();
        previouslyFocused.current = null;
      }
      return undefined;
    }

    previouslyFocused.current = document.activeElement;
    const nav = navRef.current;
    if (!nav) return undefined;

    const focusable = nav.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const list = Array.from(focusable);
    list[0]?.focus();

    const onKeyDown = (e) => {
      if (e.key !== 'Tab' || !list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  const isOverlay = isHome && !isScrolled && !isMenuOpen;

  const headerClass = [
    'header',
    isOverlay ? 'overlay' : '',
    isScrolled ? 'scrolled' : '',
    isMenuOpen ? 'menu-open' : '',
    scrollDirection === 'down' && isScrolled && !isMenuOpen ? 'hidden' : 'visible',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {isMenuOpen && (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="Close menu"
          onClick={closeMenu}
        />
      )}
      <header className={headerClass} role="banner">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <a
                href="/"
                onClick={handleLogoClick}
                className="logo-link"
                aria-label="LogitsLab — home"
              >
                <img
                  src="/assets/images/brand/logitslab-logo.png"
                  alt=""
                  className="logo-img"
                  width="40"
                  height="40"
                />
                <span className="logo-wordmark">
                  LogitsLab<span className="logo-dot">.</span>
                </span>
              </a>
            </div>

            {/* Desktop nav stays in the bar; mobile drawer is portaled below */}
            <nav
              className="nav nav-desktop"
              aria-label="Main"
            >
              <ul className="nav-list">
                {NAVIGATION_ITEMS.map((item) => (
                  <li key={item.id} className="nav-item">
                    <Link
                      to={item.href}
                      className={`nav-link ${
                        location.pathname === item.href ? 'active' : ''
                      }`}
                      aria-current={
                        location.pathname === item.href ? 'page' : undefined
                      }
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="header-actions">
              <button
                type="button"
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label={
                  theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
                }
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={theme}
                    initial={{ opacity: 0, rotate: -40, scale: 0.7 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: 40, scale: 0.7 }}
                    transition={{ duration: 0.25 }}
                    style={{ display: 'inline-flex' }}
                  >
                    {theme === 'dark' ? <FaSun size={14} /> : <FaMoon size={14} />}
                  </motion.span>
                </AnimatePresence>
              </button>

              <Link to="/connect" className="get-started-btn" onClick={closeMenu}>
                Talk to us
              </Link>

              <button
                ref={menuToggleRef}
                type="button"
                className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
                onClick={toggleMenu}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
                aria-controls={NAV_ID}
              >
                {isMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav
        id={NAV_ID}
        ref={navRef}
        className={`nav nav-drawer ${isMenuOpen ? 'nav-open' : ''}`}
        role="navigation"
        aria-label="Mobile"
        aria-hidden={!isMenuOpen}
      >
        <ul className="nav-list">
          {NAVIGATION_ITEMS.map((item) => (
            <li key={item.id} className="nav-item">
              <Link
                to={item.href}
                className={`nav-link ${
                  location.pathname === item.href ? 'active' : ''
                }`}
                onClick={closeMenu}
                tabIndex={isMenuOpen ? 0 : -1}
                aria-current={
                  location.pathname === item.href ? 'page' : undefined
                }
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to="/connect"
          className="get-started-btn-mobile"
          onClick={closeMenu}
          tabIndex={isMenuOpen ? 0 : -1}
        >
          Talk to us
        </Link>
      </nav>
    </>
  );
};

export default memo(Header);
